import './NotificationCenter.scss';

import React, { useCallback, useEffect, useState } from 'react';
import { Button, Icon, Popup } from 'semantic-ui-react';
import { NotificationBellIcon } from '../../icons/icons';
import NotificationHeader from './NotificationHeader';
import classNames from 'classnames';
import { ActionMenuType, NotificationData, NotificationsDispatch } from '../notifications-types';
import { useDispatch } from 'react-redux';
import { openNotificationCenter, setDeleteModalOpen } from '../actions';
import NotificationDeleteModal from './NotificationDeleteModal';
import useNotificationData from '../hooks/useNotificationData';
import { useAppSelector } from '../../hooks/ReduxHooks';
import NotificationTabPane from './NotificationTabPane';
import useRealTimeNotifications from '../hooks/useRealTimeNotifications';
import { analyticsTrack } from '../../helpers/AnalyticsUtility';

export default function NotificationCenter() {
  const { loadingText } = useRealTimeNotifications();
  const { notificationDataList, fetchNotifications } = useNotificationData();
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [actionMenuType, setActionMenuType] = useState<ActionMenuType | null>(null);
  const [notificationsToDelete, setNotificationsToDelete] = useState<NotificationData[] | null>(null);
  const dispatch = useDispatch<NotificationsDispatch>();
  const { deleteModalOpen, notificationCenterOpen } = useAppSelector((state) => state.notificationCenter);
  const isAdmin = useAppSelector((state) => state.profile.isAdmin);
  const hasUnread = notificationDataList.some((x) => !x.read);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleNotificationCenterOpen = (isOpen: boolean) => () => {
    dispatch(openNotificationCenter(isOpen));
  };

  const handleCloseOnOffClick = useCallback(() => {
    return !actionMenuOpen && !deleteModalOpen;
  }, [actionMenuOpen, deleteModalOpen]);

  const handleActionMenuOpen = (isOpen: boolean) => () => {
    setActionMenuOpen(isOpen);
  };

  const handleAnalytics = () => {
    analyticsTrack('Notification Bell - Selected', {
      Read: notificationDataList.filter((x) => x.read).length,
      Unread: notificationDataList.filter((x) => !x.read).length
    });
  };

  const handleNotificationDeleteModal = useCallback(
    (isOpen: boolean, notifications: NotificationData[], actionMenuType?: ActionMenuType) => () => {
      setNotificationsToDelete(notifications);
      setActionMenuType(actionMenuType);
      dispatch(setDeleteModalOpen(isOpen));
    },
    [dispatch]
  );

  return (
    <>
      <Popup
        className={classNames('notification-center', { 'depth-zero': deleteModalOpen })}
        closeOnDocumentClick={handleCloseOnOffClick()}
        closeOnTriggerClick={handleCloseOnOffClick()}
        open={notificationCenterOpen}
        on="click"
        onOpen={handleAnalytics}
        basic
        position="bottom right"
        onClose={handleNotificationCenterOpen(false)}
        trigger={
          <Button
            className={classNames('notification-center-button', { unread: hasUnread, open: notificationCenterOpen })}
            onClick={handleNotificationCenterOpen(true)}
            id="notification-center"
            tabIndex={0}
          >
            <NotificationBellIcon />
          </Button>
        }
      >
        <div className="notification-center-container">
          <Icon name="caret up" />
          <NotificationHeader isAdmin={isAdmin} />
          <NotificationTabPane
            notificationDataList={notificationDataList}
            hasUnread={hasUnread}
            loadingText={loadingText}
            dispatch={dispatch}
            handleNotificationCenterOpen={handleNotificationCenterOpen}
            handleActionMenuOpen={handleActionMenuOpen}
            handleNotificationDeleteModal={handleNotificationDeleteModal}
          />
        </div>
      </Popup>
      {/* only render the delete notification modal when an action is performed */}
      {deleteModalOpen && (
        <NotificationDeleteModal
          deleteModalOpen={deleteModalOpen}
          notificationDataList={notificationsToDelete}
          actionMenuType={actionMenuType}
          dispatch={dispatch}
        />
      )}
    </>
  );
}
